import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent text-sm font-medium transition-all no-underline hover:no-underline aria-disabled:cursor-default [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive focus-visible:aria-invalid:ring-ring/50 focus-visible:aria-invalid:border-ring",
  {
    variants: {
      variant: {
        primary:
          "bg-action-primary text-action-primary-foreground not-aria-disabled:hover:bg-action-primary-hover",
        // A pressed toggle fills, so a row of presets shows which one holds.
        secondary:
          "bg-background text-foreground border-input shadow-xs not-aria-disabled:hover:bg-accent aria-pressed:bg-accent aria-pressed:border-foreground/40",
        // Brand ink: with no border or fill, it is the colour alone that tells
        // a row action apart from the cell text and status glyphs beside it.
        tertiary:
          "bg-transparent text-brand-primary not-aria-disabled:hover:bg-brand-primary/10 aria-pressed:bg-brand-primary/10",
      },
      destructive: {
        true: "",
        false: "",
      },
      // A confirming row or inline action (Approve, Restore); tertiary only.
      success: {
        true: "",
        false: "",
      },
      size: {
        xs: "h-6 gap-1 px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-7 gap-1 px-2.5",
        md: "h-8 px-3",
        lg: "h-10 px-6",
      },
      iconOnly: {
        true: "px-0",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        destructive: true,
        class:
          "bg-destructive text-white not-aria-disabled:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
      },
      {
        variant: "secondary",
        destructive: true,
        class:
          "text-destructive-text border-destructive-text not-aria-disabled:hover:bg-destructive-text/10",
      },
      {
        variant: "tertiary",
        destructive: true,
        class:
          "text-destructive-text not-aria-disabled:hover:bg-destructive-text/10",
      },
      // `success-foreground`, not `success-solid`: the solid is under 4.5:1 as text.
      {
        variant: "tertiary",
        success: true,
        class:
          "text-success-foreground not-aria-disabled:hover:bg-success-foreground/10 aria-pressed:bg-success-foreground/10",
      },
      { iconOnly: true, size: "xs", class: "w-6" },
      { iconOnly: true, size: "sm", class: "w-7" },
      { iconOnly: true, size: "md", class: "w-8" },
      { iconOnly: true, size: "lg", class: "w-10" },
    ],
    defaultVariants: {
      variant: "secondary",
      destructive: false,
      success: false,
      size: "md",
      iconOnly: false,
    },
  },
);

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;

export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>["size"]
>;

function Button({
  className,
  variant = "secondary",
  size = "md",
  destructive,
  success,
  iconOnly,
  asChild = false,
  pending = false,
  disabled: disabledProp,
  children,
  onClick,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pending?: boolean;
  }) {
  const classes = buttonVariants({
    variant,
    size,
    destructive,
    success,
    iconOnly,
  });

  // When asChild is true, Slot expects a single child element
  if (asChild) {
    return (
      <Slot
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(classes, className)}
        {...props}
      >
        {children}
      </Slot>
    );
  }

  const disabled = disabledProp || pending;
  // Tertiary has no loading state of its own: while busy it reads as
  // unavailable, like a disabled button, rather than spinning.
  const spinner = pending && variant !== "tertiary";

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-disabled={disabled} // Custom `disabled` impl to keep mouse hover events
      aria-busy={pending || undefined}
      tabIndex={disabled ? -1 : undefined}
      onClick={disabled ? (event) => event.preventDefault() : onClick}
      className={cn(
        "relative",
        classes,
        disabled && !spinner && "opacity-50",
        className,
      )}
      {...props}
    >
      {/* Transparent rather than `invisible`: hidden content drops out of the
          accessible name, and the button would go nameless while it spins. */}
      <span
        className={cn(
          "inline-flex items-center gap-[inherit]",
          spinner && "opacity-0",
        )}
      >
        {children}
      </span>

      {spinner && (
        <span
          data-slot="button-spinner"
          className="absolute inset-0 flex items-center justify-center"
        >
          <LoaderCircle aria-hidden className="animate-spin" />
        </span>
      )}
    </button>
  );
}

export { Button, buttonVariants };
